import { __decorate } from "tslib";
import { html, nothing } from 'lit';
import { customElement, property, query, queryAssignedNodes, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { localized, msg } from '@lit/localize';
import '../icon/icon.js';
import '../text-field/text-field.js';
import { passwordStyles } from './password.styles.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { provide } from '@lit/context';
import { fieldValueContext, pristineFieldContext } from './password-checklist-item-context.js';
import { HlxPasswordChecklistItem } from './password-checklist-item.js';
import HelixElement from '../../shared/types/helix-element.js';
import { emitInvalidEvent, findForm, submitForm } from '../../shared/forms/form-utils.js';
import { FormDataController } from '../../shared/forms/form-data.controller.js';
import { ValidationController } from '../../shared/forms/validation.controller.js';
let HlxPassword = class HlxPassword extends HelixElement {
    constructor() {
        super();
        this.isPristine = true;
        this.inputType = 'password';
        this.progressAmount = 0;
        this.label = '';
        this.showClearIcon = false;
        this.getPasswordQualityState = this.getDefaultPasswordQualityState;
        this.name = '';
        this.value = '';
        this.placeholder = '';
        this.errorText = '';
        this.helpText = '';
        this.required = false;
        this.disabled = false;
        this.showProgress = false;
        this.readonly = false;
        this.title = '';
        this.autocomplete = 'on';
        this.size = 'medium';
        this.slotController = new SlotController(this, 'label', 'error-text', 'help-text', 'password-checklist');
        new FormDataController(this);
        new ValidationController(this);
    }
    render() {
        return html `
            <div part="hlx-form-control">
                <hlx-text-field
                    class=${classMap({
            'hlx-password-control': true,
        })}
                    type=${this.inputType}
                    name=${ifDefined(this.name)}
                    value=${this.value}
                    placeholder=${ifDefined(this.placeholder)}
                    title=${this.title}
                    ?required=${this.required}
                    ?show-error=${this.showError}
                    autocomplete=${ifDefined(this.autocomplete)}
                    label=${ifDefined(this.label)}
                    error-text=${ifDefined(this.errorText)}
                    help-text=${ifDefined(this.helpText)}
                    size=${this.size}
                    ?disabled=${this.disabled}
                    ?readonly=${this.readonly}
                    @hlx-change=${this.handleChange}
                    @hlx-blur=${this.handleBlur}
                    @hlx-focus=${this.handleFocus}
                    @hlx-input=${this.handleInput}
                    @hlx-invalid=${this.handleInvalid}
                    @keydown=${this.handleKeydown}>
                    <slot name="label" slot=${this.confirmSlot('label')}></slot>
                    <slot name="error-text" slot=${this.confirmSlot('error-text')}></slot>
                    <slot name="error-icon" slot=${this.confirmSlot('error-icon')}></slot>
                    <slot name="error-mapping" slot=${this.confirmSlot('error-mapping')}></slot>
                    <slot name="help-text" slot=${this.confirmSlot('help-text')}></slot>
                    ${this.disabled || this.readonly ?
            nothing
            : html `
                            <div class="hlx-icon-wrapper" slot="suffix">
                                ${this.showClearIcon ?
                html `
                                        <hlx-icon
                                            part="hlx-clear-icon"
                                            class="hlx-input-icon"
                                            data-testid="clear-icon"
                                            @click=${this.clearPassword}
                                            title=${msg('Clear password field')}
                                            name="xmark"></hlx-icon>
                                    `
                : nothing}
                                <hlx-icon
                                    part="hlx-toggle-input-type-icon"
                                    class="hlx-input-icon"
                                    data-testid="toggle-input-type-icon"
                                    @click=${this.toggleInputType}
                                    title="${this.inputType === 'password' ?
                msg('Show password')
                : msg('Hide password')}"
                                    name=${this.inputType === 'password' ?
                'eye'
                : 'eye-slash'}></hlx-icon>
                            </div>
                        `}
                </hlx-text-field>

                ${(this.slotController.hasSlot('password-checklist') &&
            this.showProgress &&
            !this.isPristine) ?
            html `
                        <div
                            part="hlx-progress-bar"
                            class="hlx-progress"
                            data-testid="progress-bar">
                            ${this.passwordQuality ?
                html `
                                    <div
                                        class="hlx-progress-quality"
                                        data-testid="password-quality"
                                        style=${styleMap({
                    color: this.progressBarColor,
                })}>
                                        ${this.passwordQuality}
                                    </div>
                                `
                : nothing}

                            <div class="hlx-progress-outer">
                                <div
                                    class="hlx-progress-inner"
                                    data-testid="progress-inner"
                                    style=${styleMap({
                width: this.progressAmount + '%',
                'background-color': this.progressBarColor,
            })}></div>
                            </div>
                        </div>
                    `
            : ''}
                ${this.qualityDescription ?
            html `
                        <div
                            part="hlx-quality-description"
                            class="hlx-progress-quality-description"
                            data-testid="quality-description">
                            ${this.qualityDescription}
                        </div>
                    `
            : ''}
                <slot
                    class=${classMap({
            'hlx-password-checklist': this.slotController.hasSlot('password-checklist'),
        })}
                    name="password-checklist"></slot>
            </div>
        `;
    }
    async getUpdateComplete() {
        await super.getUpdateComplete();
        await this.input.updateComplete;
        return true;
    }
    get validity() {
        return this.input.validity;
    }
    get validationMessage() {
        return this.input.validationMessage;
    }
    checkValidity() {
        return this.input.checkValidity();
    }
    reportValidity() {
        return this.input.reportValidity();
    }
    setCustomValidity(message) {
        this.input.setCustomValidity(message);
    }
    getForm() {
        return findForm(this);
    }
    focus(options) {
        this.input.focus(options);
    }
    handleKeydown(e) {
        if (e.key === 'Enter') {
            submitForm(this.getForm());
        }
    }
    handleChange(event) {
        this.value = event.target.value;
        this.emit('hlx-change');
    }
    handleBlur() {
        this.emit('hlx-blur');
    }
    handleFocus() {
        this.emit('hlx-focus');
    }
    handleInput(event) {
        this.isPristine = false;
        this.value = event.target.value;
        this.setProgressQuality();
        this.emit('hlx-input');
    }
    handleInvalid(event) {
        emitInvalidEvent(this, event);
    }
    confirmSlot(name) {
        return this.slotController.hasSlot(name) ? name : nothing;
    }
    getDefaultPasswordQualityState(value) {
        const children = this.checklistNode.length ? Array.from(this.checklistNode[0].children) : [];
        const checklistItems = children.filter((element) => element instanceof HlxPasswordChecklistItem);
        let errorCount = 0;
        checklistItems.forEach((checklistComponent) => {
            if (!checklistComponent.validateFn(value)) {
                errorCount += 1;
            }
        });
        const progressAmount = 100 - (errorCount * 100) / checklistItems.length;
        return {
            passwordQuality: '',
            progressAmount,
            qualityDescription: '',
            progressBarColor: this.getDefaultProgressBarColorByPercent(progressAmount),
        };
    }
    getDefaultProgressBarColorByPercent(progressAmount) {
        if (progressAmount <= 25) {
            return 'var(--hlx-color-error-default)';
        }
        if (progressAmount <= 50) {
            return 'var(--hlx-color-orange-70)';
        }
        if (progressAmount <= 75) {
            return 'var(--hlx-color-info-default)';
        }
        if (progressAmount <= 100) {
            return 'var(--hlx-color-success-default)';
        }
    }
    setProgressQuality() {
        const { passwordQuality, qualityDescription, progressAmount, progressBarColor } = this.getPasswordQualityState(this.value);
        this.passwordQuality = passwordQuality;
        this.qualityDescription = qualityDescription;
        this.progressAmount = progressAmount > 100 ? 100 : progressAmount;
        this.progressBarColor = progressBarColor;
    }
    toggleInputType() {
        this.inputType = this.inputType === 'password' ? 'text' : 'password';
    }
    clearPassword() {
        this.value = '';
        this.emit('hlx-input');
        this.setProgressQuality();
    }
};
HlxPassword.styles = [componentStyles, passwordStyles];
__decorate([
    query('.hlx-password-control')
], HlxPassword.prototype, "input", void 0);
__decorate([
    provide({ context: pristineFieldContext }),
    state()
], HlxPassword.prototype, "isPristine", void 0);
__decorate([
    state()
], HlxPassword.prototype, "inputType", void 0);
__decorate([
    state()
], HlxPassword.prototype, "passwordQuality", void 0);
__decorate([
    state()
], HlxPassword.prototype, "progressAmount", void 0);
__decorate([
    state()
], HlxPassword.prototype, "progressBarColor", void 0);
__decorate([
    state()
], HlxPassword.prototype, "qualityDescription", void 0);
__decorate([
    property({ type: String })
], HlxPassword.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-clear-icon' })
], HlxPassword.prototype, "showClearIcon", void 0);
__decorate([
    property({ type: Function })
], HlxPassword.prototype, "getPasswordQualityState", void 0);
__decorate([
    property({ type: String })
], HlxPassword.prototype, "form", void 0);
__decorate([
    property({ type: String })
], HlxPassword.prototype, "name", void 0);
__decorate([
    provide({ context: fieldValueContext }),
    property({ type: String })
], HlxPassword.prototype, "value", void 0);
__decorate([
    property({ type: String })
], HlxPassword.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'error-text' })
], HlxPassword.prototype, "errorText", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-error' })
], HlxPassword.prototype, "showError", void 0);
__decorate([
    property({ type: String, attribute: 'help-text' })
], HlxPassword.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxPassword.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxPassword.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-progress', reflect: true })
], HlxPassword.prototype, "showProgress", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxPassword.prototype, "readonly", void 0);
__decorate([
    property({ type: String })
], HlxPassword.prototype, "title", void 0);
__decorate([
    property({ type: String })
], HlxPassword.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxPassword.prototype, "size", void 0);
__decorate([
    queryAssignedNodes({ slot: 'password-checklist', flatten: true })
], HlxPassword.prototype, "checklistNode", void 0);
HlxPassword = __decorate([
    customElement('hlx-password'),
    localized()
], HlxPassword);
export { HlxPassword };
//# sourceMappingURL=password.js.map